import { Button } from '@ooh/ui/components/button';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { fmtPln } from './client-offer-review-format';
import type { ClientOfferReviewConfirmationModalsLabels } from './client-offer-review-types';

export type ClientOfferReviewModalKind = 'selectAll' | 'clearAll' | 'accept' | null;

// z-[60] is a fixed Tailwind stacking-context utility, not user-facing text.
const MODAL_Z_INDEX = 'z-[60]';

/**
 * Select-all, clear-all, and accept-offer confirmation modals. The
 * whole-offer reject confirmation was removed alongside the reject action
 * (offer-versioning-diff / Version-Specific Reject Button UI, REMOVED).
 */
export function ClientOfferReviewConfirmationModals({
  modal,
  onClose,
  availCount,
  accCount,
  rejCount,
  pendCount,
  accValue,
  versionNumber,
  onConfirmSelectAll,
  onConfirmClearAll,
  onConfirmAccept,
  labels,
}: {
  modal: ClientOfferReviewModalKind;
  onClose: () => void;
  availCount: number;
  accCount: number;
  rejCount: number;
  pendCount: number;
  accValue: number;
  versionNumber: number;
  onConfirmSelectAll: () => void;
  onConfirmClearAll: () => void;
  onConfirmAccept: () => void;
  labels: ClientOfferReviewConfirmationModalsLabels;
}) {
  if (modal === 'selectAll') {
    return (
      <ModalDialog
        header={<span className="text-sm font-semibold">{labels.selectAllTitle}</span>}
        onClose={onClose}
        zIndex={MODAL_Z_INDEX}
        testId="select-all-modal"
        footer={
          <>
            <Button variant="outline" onClick={onClose}>
              {labels.selectAllCancel}
            </Button>
            <Button variant="success" onClick={onConfirmSelectAll} data-testid="confirm-select-all">
              {labels.selectAllConfirm}
            </Button>
          </>
        }
      >
        <p className="text-sm leading-relaxed text-muted-foreground">
          {labels.selectAllBody(availCount)}
        </p>
      </ModalDialog>
    );
  }

  if (modal === 'clearAll') {
    return (
      <ModalDialog
        header={<span className="text-sm font-semibold">{labels.clearAllTitle}</span>}
        onClose={onClose}
        zIndex={MODAL_Z_INDEX}
        testId="clear-all-modal"
        footer={
          <>
            <Button variant="outline" onClick={onClose}>
              {labels.clearAllCancel}
            </Button>
            <Button
              variant="destructive"
              onClick={onConfirmClearAll}
              data-testid="confirm-clear-all"
            >
              {labels.clearAllConfirm}
            </Button>
          </>
        }
      >
        <p className="text-sm leading-relaxed text-muted-foreground">
          {labels.clearAllBody(accCount, rejCount)}
        </p>
      </ModalDialog>
    );
  }

  if (modal === 'accept') {
    return (
      <ModalDialog
        header={
          <span className="text-sm font-semibold">{labels.acceptOfferTitle(versionNumber)}</span>
        }
        onClose={onClose}
        zIndex={MODAL_Z_INDEX}
        testId="accept-offer-modal"
        footer={
          <>
            <Button variant="outline" onClick={onClose}>
              {labels.acceptOfferCancel}
            </Button>
            <Button variant="success" onClick={onConfirmAccept} data-testid="confirm-accept-offer">
              {labels.acceptOfferConfirm}
            </Button>
          </>
        }
      >
        <p className="text-sm leading-relaxed text-muted-foreground">
          {labels.acceptOfferBody(accCount, rejCount, pendCount, fmtPln(accValue) ?? '')}
        </p>
      </ModalDialog>
    );
  }

  return null;
}
